/**
 * @fileoverview Provide the shared read-only Drive evidence surface for Intake and Inbox. Callers own the document list and workflow actions; local selection only chooses the preview. Google/Drive URL access remains external to this display boundary, and a preview is distinct from a managed archive edit.
 */

import React, { useEffect, useMemo, useState } from 'react';
import AppIcon from '../shared/AppIcon';
import './DriveDocumentPreview.css';



function googlePreviewUrl(href, driveFileId) {
  if (!href && driveFileId) return `https://drive.google.com/file/d/${driveFileId}/preview`;
  if (!href) return '';
  try {
    const url = new URL(href);
    if (url.hostname === 'drive.google.com' && /\/file\/d\/[^/]+/.test(url.pathname)) {
      return `${url.origin}${url.pathname.replace(/\/view.*$/, '/preview')}`;
    }
    if (url.hostname === 'docs.google.com' && /\/(document|spreadsheets|presentation)\/d\/[^/]+/.test(url.pathname)) {
      return `${url.origin}${url.pathname.replace(/\/(edit|view).*$/, '/preview')}`;
    }
  } catch (_) {
    return '';
  }
  return '';
}


export default function DriveDocumentPreview({ documents = [], getHref, getTitle, getMeta, empty = 'No documents are recorded.' }) {
  const activeDocuments = useMemo(() => documents.filter((document) => document.status !== 'archived'), [documents]);
  const [selectedId, setSelectedId] = useState(null);
  const selected = activeDocuments.find((document) => document.id === selectedId) || null;

  useEffect(() => {
    if (selectedId && activeDocuments.some((document) => document.id === selectedId)) return;
    setSelectedId(null);
  }, [activeDocuments, selectedId]);

  if (!activeDocuments.length) return <div className="drive-document-preview__empty">{empty}</div>;

  const selectedHref = selected ? getHref(selected) : '';
  const previewHref = selected ? googlePreviewUrl(selectedHref, selected.google_drive_file_id) : '';

  return <section className="drive-document-preview" aria-label="Document preview">
    <ol className="drive-document-preview__list">
      {activeDocuments.map((document) => {
        const isSelected = document.id === selectedId;
        return <li key={document.id}>
          <button type="button" className={isSelected ? 'drive-document-preview__item drive-document-preview__item--selected' : 'drive-document-preview__item'} onClick={() => setSelectedId(document.id)} aria-pressed={isSelected}>
            <span><strong>{getTitle(document)}</strong>{getMeta ? <small>{getMeta(document)}</small> : null}</span>
            <AppIcon icon="pi pi-eye" aria-hidden="true" />
          </button>
        </li>;
      })}
    </ol>
    {selected ? <div className="drive-document-preview__viewer">
      <div className="drive-document-preview__viewer-header">
        <strong>{getTitle(selected)}</strong>
        {selectedHref ? <a href={selectedHref} target="_blank" rel="noreferrer" className="intake-button"><AppIcon icon="pi pi-external-link" aria-hidden="true" /><span>Open in Drive</span></a> : null}
      </div>
      {previewHref ? <iframe title={`Preview: ${getTitle(selected)}`} src={previewHref} className="drive-document-preview__frame" /> : <div className="drive-document-preview__fallback">This file cannot be previewed here. Use Open in Drive.</div>}
    </div> : <div className="drive-document-preview__empty">Select a document to preview it here.</div>}
  </section>;
}
